import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  createSourcesUsesScenarioApi,
  getCashFlowFeeExposureApi,
  getDealsApi,
  getSourcesUsesCompanyCashApi,
  overwriteSourcesUsesScenarioApi,
  savePipelineExposureBatchApi,
  saveSourcesUsesCompanyCashApi,
} from 'api/dealApi';
import Card from 'components/card/Card';
import { HelpLink } from 'components/HelpLink';
import { Currency } from 'components/Number';
import { pickLatestDeal } from 'pages/Cockpit/AccountingInternal/calc/pickLatestDeal';
import PropTypes from 'prop-types';
import {
  MATRIX_ROWS,
  NEGATIVE_DISPLAY_KEYS,
  aggregateSeries,
  displaySignedAmount,
  excelSheetName,
  filterMonthsByDateRange,
  forecastStartMonth,
  formatAccountingCurrency,
  formatGrowthConstraintTooltip,
  formatMonthLabel,
  formatFeeBreakdownTooltip,
  formatReturnsBreakdownTooltip,
  isFundingConstraintIssue,
  resolveHorizon,
  writeCashFlowExportSheet,
  writeCohortRepaymentExportSheet,
} from './cashflowFeeExposureHelpers';
import {
  writeLiveRepaymentSheet,
  writeFundingSheet,
  writePipelineRepaymentSheet,
  writeSourcesAndUsesSheet,
} from './cashflowFeeExposureExport';
import PipelineClientsTable from './PipelineClientsTable';
import CohortRepaymentSection from './CohortRepaymentSection';
import FacilityFeeTable from './FacilityFeeTable';
import FeeCalculationTable from './FeeCalculationTable';
import { FEE_METHODS } from './feeCalculation';
import { facilityFeeMonthKeys } from './facilityFeeSchedule';
import SourcesAndUsesTable from './SourcesAndUsesTable';
import MarketingUsedTable from './MarketingUsedTable';
import CompanyCashStatement from './CompanyCashStatement';
import {
  EMPTY_COMPANY_CASH_INPUTS,
  customerCashSeries,
  monthRange,
  withLenders,
} from './companyCash';
import { writeCompanyCashSheet } from './companyCashExcel';
import {
  writeConfigurationSheet,
  writeFacilityFeeSheet,
  writeFeeSheet,
  writeMarketingSheet,
  writeRepaymentVectorsSheet,
} from './sourcesUsesSheets';
import { useUserName } from './useUserName';
import { customerStage, orderedStages, shownChartCustomer, shownPipelineClient } from './pipelineExposure';

const BRAND = '#422AFB';
const POSITIVE = '#01B574';
const NEGATIVE = '#D1352A';
const SURFACE = '#F4F7FE';
const AXIS = '#A3AED0';
const CHART_MONTH_SLOT = 96;
const CHART_PAD = { t: 20, r: 72, b: 52, l: 64 };

export function trendChartWidth(containerWidth, monthCount) {
  const slots = Math.max((monthCount || 1) - 1, 1);
  return Math.max(containerWidth || 0, CHART_PAD.l + CHART_PAD.r + slots * CHART_MONTH_SLOT);
}

function formatChartCurrency(value) {
  return Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

function CustomerMultiSelect({ customers, selectedIds, onChange }) {
  const [search, setSearch] = useState('');
  const filtered = customers.filter((c) =>
    (c.companyName || '').toLowerCase().includes(search.toLowerCase())
  );
  const allSelected = customers.length > 0 && selectedIds.length === customers.length;
  const triggerLabel = () => {
    if (selectedIds.length === 0 || allSelected) return 'All customers';
    if (selectedIds.length === 1) {
      return customers.find((c) => c.customerId === selectedIds[0])?.companyName || '1 customer';
    }
    return `${selectedIds.length} customers`;
  };

  return (
    <Menu closeOnSelect={false} strategy="fixed">
      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" minW="220px">
        {triggerLabel()}
      </MenuButton>
      <Portal>
      <MenuList zIndex={1400} maxH="320px" overflowY="auto" minW="280px" p={3}>
        <Input
          size="sm"
          mb={2}
          placeholder="Search by Company Name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Flex gap={2} mb={2}>
          <Button
            size="xs"
            onClick={() => onChange(customers.map((c) => c.customerId))}
          >
            Select All
          </Button>
          <Button size="xs" variant="ghost" onClick={() => onChange([])}>
            Clear
          </Button>
        </Flex>
        {filtered.map((c) => {
          const checked = selectedIds.includes(c.customerId);
          return (
            <Flex key={c.customerId} align="center" py={1}>
              <Checkbox
                isChecked={checked}
                onChange={() => {
                  if (checked) onChange(selectedIds.filter((id) => id !== c.customerId));
                  else onChange([...selectedIds, c.customerId]);
                }}
              >
                {c.companyName}
              </Checkbox>
            </Flex>
          );
        })}
        <Text mt={2} fontSize="xs" color="gray.500">
          {selectedIds.length} of {customers.length} selected
        </Text>
      </MenuList>
      </Portal>
    </Menu>
  );
}

CustomerMultiSelect.propTypes = {
  customers: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

function StageMultiSelect({ stages, selectedStages, onChange }) {
  const selected = selectedStages == null ? stages : selectedStages;
  const allSelected = stages.length > 0 && selected.length === stages.length;
  const triggerLabel = () => {
    if (selected.length === 0) return 'No stages';
    if (allSelected) return 'All stages';
    if (selected.length === 1) return selected[0];
    return `${selected.length} stages`;
  };

  return (
    <Menu closeOnSelect={false} strategy="fixed">
      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" minW="180px">
        {triggerLabel()}
      </MenuButton>
      <Portal>
      <MenuList zIndex={1400} maxH="320px" overflowY="auto" minW="240px" p={3}>
        <Flex gap={2} mb={2}>
          <Button size="xs" onClick={() => onChange(null)}>
            Select All
          </Button>
          <Button size="xs" variant="ghost" onClick={() => onChange([])}>
            Clear
          </Button>
        </Flex>
        {stages.map((stage) => {
          const checked = selected.includes(stage);
          return (
            <Flex key={stage} align="center" py={1}>
              <Checkbox
                isChecked={checked}
                onChange={() => {
                  const current = selectedStages == null ? stages : selectedStages;
                  if (checked) onChange(current.filter((item) => item !== stage));
                  else onChange([...current, stage]);
                }}
              >
                {stage}
              </Checkbox>
            </Flex>
          );
        })}
      </MenuList>
      </Portal>
    </Menu>
  );
}

StageMultiSelect.propTypes = {
  stages: PropTypes.array.isRequired,
  selectedStages: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

function TrendChart({ months }) {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect?.width || 0);
      setWidth(next);
    });
    observer.observe(el);
    setWidth(Math.floor(el.clientWidth || 0));
    return () => observer.disconnect();
  }, []);

  if (!months?.length) return null;

  const height = 248;
  const pad = CHART_PAD;
  const chartWidth = trendChartWidth(width, months.length);
  const innerW = Math.max(chartWidth - pad.l - pad.r, 1);
  const innerH = height - pad.t - pad.b;
  const ending = months.map((m) => m.exposureEndingBalance || 0);
  let cumFee = 0;
  const fees = months.map((m) => {
    cumFee += m.fee || 0;
    return cumFee;
  });
  // Exposure uses the left axis; cumulative fee has its own scale on the right.
  const maxY = Math.max(...ending, 1);
  // Headroom so the fee line peaks at about two thirds of the height, below the exposure line.
  const maxFee = Math.max(...fees, 1) * 1.5;
  const x = (i) => pad.l + (months.length === 1 ? innerW / 2 : (i / (months.length - 1)) * innerW);
  const y = (v) => pad.t + innerH - (v / maxY) * innerH;
  const yFee = (v) => pad.t + innerH - (v / maxFee) * innerH;
  const area = ending
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`)
    .join(' ');
  const areaClose = `${area} L ${x(months.length - 1)} ${pad.t + innerH} L ${x(0)} ${pad.t + innerH} Z`;
  const feeLine = fees.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${yFee(v)}`).join(' ');
  const forecastIndex = months.findIndex((m) => m.month === forecastStartMonth());
  const todayX = forecastIndex > 0
    ? (x(forecastIndex - 1) + x(forecastIndex)) / 2
    : forecastIndex === 0
      ? pad.l
      : null;
  const yTicks = [0, 0.5, 1].map((t) => t * maxY);
  const feeTicks = [0, 0.5, 1].map((t) => t * maxFee);
  const hover = hoverIndex != null ? months[hoverIndex] : null;

  const onMove = (event) => {
    if (dragRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    let nearest = 0;
    let best = Infinity;
    for (let i = 0; i < months.length; i += 1) {
      const d = Math.abs(x(i) - px);
      if (d < best) {
        best = d;
        nearest = i;
      }
    }
    setHoverIndex(nearest);
  };

  const canSlide = chartWidth > width + 1;

  const onPointerDown = (event) => {
    if (!canSlide || event.button !== 0 || !scrollRef.current) return;
    dragRef.current = { x: event.clientX, scroll: scrollRef.current.scrollLeft };
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || !scrollRef.current) return;
    scrollRef.current.scrollLeft = drag.scroll - (event.clientX - drag.x);
  };

  const endDrag = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <Box ref={containerRef} w="100%" bg={SURFACE} borderRadius="20px" p={3} position="relative">
      <Flex gap={5} align="center" mb={2} px={1} fontSize="xs" color="gray.600" fontFamily="body">
        <Flex align="center" gap={2}>
          <Box w="14px" h="3px" bg={BRAND} borderRadius="full" />
          <Text>Exposure ending balance (left axis)</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Box w="14px" h="3px" bg={POSITIVE} borderRadius="full" />
          <Text>Cumulative fee (right axis)</Text>
        </Flex>
      </Flex>
      {width > 0 && (
        <Box
          ref={scrollRef}
          overflowX="auto"
          cursor={dragging ? 'grabbing' : canSlide ? 'grab' : 'crosshair'}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <Box position="relative" w={`${chartWidth}px`} minW="100%">
          <svg
            width={chartWidth}
            height={height}
            style={{ display: 'block' }}
            onMouseMove={onMove}
            onMouseLeave={() => setHoverIndex(null)}
          >
            {yTicks.map((tick) => (
              <g key={`yt-${tick}`}>
                <line
                  x1={pad.l}
                  x2={pad.l + innerW}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke={AXIS}
                  strokeOpacity="0.35"
                />
              </g>
            ))}
            <line
              x1={pad.l}
              x2={pad.l}
              y1={pad.t}
              y2={pad.t + innerH}
              stroke={AXIS}
              strokeWidth="1"
            />
            <line
              x1={pad.l}
              x2={pad.l + innerW}
              y1={pad.t + innerH}
              y2={pad.t + innerH}
              stroke={AXIS}
              strokeWidth="1.5"
            />
            <text
              x={pad.l + innerW / 2}
              y={height - 8}
              textAnchor="middle"
              fontSize="11"
              fill={AXIS}
              fontFamily="DM Sans, sans-serif"
            >
              Month
            </text>
            <path d={areaClose} fill={BRAND} opacity="0.12" />
            <path d={area} fill="none" stroke={BRAND} strokeWidth="2" />
            <path d={feeLine} fill="none" stroke={POSITIVE} strokeWidth="2" />
            {todayX != null && (
              <>
                <line
                  x1={todayX}
                  x2={todayX}
                  y1={pad.t}
                  y2={pad.t + innerH}
                  stroke={AXIS}
                  strokeDasharray="4 4"
                />
                <rect
                  x={todayX}
                  y={pad.t}
                  width={Math.max(0, pad.l + innerW - todayX)}
                  height={innerH}
                  fill={AXIS}
                  opacity="0.08"
                />
              </>
            )}
            {months.map((m, i) => (
              <g key={m.month}>
                <circle cx={x(i)} cy={y(ending[i])} r={hoverIndex === i ? 4 : 3} fill={BRAND} />
                <circle cx={x(i)} cy={yFee(fees[i])} r={hoverIndex === i ? 4 : 3} fill={POSITIVE} />
                <text
                  x={x(i)}
                  y={pad.t + innerH + 16}
                  textAnchor="middle"
                  fontSize="10"
                  fill={AXIS}
                  fontFamily="DM Sans, sans-serif"
                >
                  {formatMonthLabel(m.month)}
                </text>
              </g>
            ))}
            {hoverIndex != null && (
              <line
                x1={x(hoverIndex)}
                x2={x(hoverIndex)}
                y1={pad.t}
                y2={pad.t + innerH}
                stroke={BRAND}
                strokeOpacity="0.35"
              />
            )}
          </svg>
          <Box
            position="sticky"
            left={0}
            w={`${pad.l}px`}
            h={`${height}px`}
            mt={`-${height}px`}
            bg={SURFACE}
            pointerEvents="none"
            zIndex={1}
          >
            {yTicks.map((tick) => (
              <Text
                key={`ylab-${tick}`}
                position="absolute"
                right="8px"
                top={`${y(tick) - 6}px`}
                fontSize="10px"
                color={AXIS}
                fontFamily="DM Sans, sans-serif"
                lineHeight="1"
              >
                {formatChartCurrency(tick)}
              </Text>
            ))}
            <Box position="absolute" right={0} top={`${pad.t}px`} h={`${innerH}px`} w="1px" bg={AXIS} />
          </Box>
          <Box
            position="sticky"
            right={0}
            ml={`${chartWidth - pad.r}px`}
            w={`${pad.r}px`}
            h={`${height}px`}
            mt={`-${height}px`}
            bg={SURFACE}
            pointerEvents="none"
            zIndex={1}
            data-testid="fee-axis"
          >
            <Box position="absolute" left={0} top={`${pad.t}px`} h={`${innerH}px`} w="1px" bg={POSITIVE} />
            {feeTicks.map((tick) => (
              <Text
                key={`yfee-${tick}`}
                position="absolute"
                left="8px"
                top={`${yFee(tick) - 6}px`}
                fontSize="10px"
                color={POSITIVE}
                fontFamily="DM Sans, sans-serif"
                lineHeight="1"
              >
                {formatChartCurrency(tick)}
              </Text>
            ))}
          </Box>
          {hover && hoverIndex != null && (
            <Box
              position="absolute"
              left={`${Math.min(Math.max(x(hoverIndex) + 12, 8), chartWidth - 210)}px`}
              top="28px"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              boxShadow="sm"
              px={3}
              py={2}
              fontSize="xs"
              fontFamily="body"
              pointerEvents="none"
              zIndex={2}
              minW="180px"
            >
              <Text fontWeight={700} mb={1}>
                {formatMonthLabel(hover.month)}
              </Text>
              <Text color={BRAND}>
                Exposure ending: {formatChartCurrency(ending[hoverIndex])}
              </Text>
              <Text color={POSITIVE}>
                Cumulative fee: {formatChartCurrency(fees[hoverIndex])}
              </Text>
            </Box>
          )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

TrendChart.propTypes = {
  months: PropTypes.array,
};

function MonthMatrix({ months, showCustomerNames = false }) {
  if (!months?.length) return null;
  const forecastMonth = forecastStartMonth();
  return (
    <Box overflowX="auto" mt={4}>
      <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
        <thead>
          <tr>
            <th
              style={{
                position: 'sticky',
                left: 0,
                background: '#fff',
                textAlign: 'left',
                padding: '8px 12px',
                zIndex: 1,
              }}
            >
              Line Item
            </th>
            {months.map((m) => (
              <th
                key={m.month}
                style={{
                  padding: '8px 12px',
                  textAlign: 'right',
                  borderLeft: m.month === forecastMonth ? `2px solid ${BRAND}` : undefined,
                }}
              >
                {formatMonthLabel(m.month)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MATRIX_ROWS.map((def) => {
            const emphasize = def.key === 'exposureEndingBalance';
            return (
              <tr key={def.key} style={{ background: emphasize ? SURFACE : undefined }}>
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: emphasize ? SURFACE : '#fff',
                    padding: '8px 12px',
                    fontWeight: emphasize ? 700 : 500,
                    zIndex: 1,
                  }}
                >
                  {def.label}
                </td>
                {months.map((m) => {
                  const rawValue = m[def.key];
                  const value = displaySignedAmount(def.key, rawValue);
                  const isZero = rawValue == null || rawValue === 0;
                  const growthConstrained =
                    def.key === 'growthInvestments' &&
                    (isFundingConstraintIssue(m.bindingConstraint) ||
                      (Array.isArray(m.fundingConstraints) && m.fundingConstraints.length > 0));
                  const breakdownTip =
                    def.key === 'returns'
                      ? formatReturnsBreakdownTooltip(m.returnsBreakdown)
                      : def.key === 'fee'
                        ? formatFeeBreakdownTooltip(m.feeBreakdown)
                        : def.key === 'growthInvestments'
                          ? formatGrowthConstraintTooltip(m, {
                              includeCustomerNames: showCustomerNames,
                            })
                          : '';
                  const cell = isZero
                    ? '—'
                    : NEGATIVE_DISPLAY_KEYS.has(def.key)
                      ? formatAccountingCurrency(value)
                      : (
                        <Currency value={value} maximumFractionDigits={0} />
                      );
                  return (
                    <td
                      key={`${def.key}-${m.month}`}
                      style={{
                        padding: '8px 12px',
                        textAlign: 'right',
                        fontWeight: emphasize ? 700 : 400,
                        color: growthConstrained
                          ? '#9C4221'
                          : !isZero && value < 0 && !NEGATIVE_DISPLAY_KEYS.has(def.key)
                            ? NEGATIVE
                            : undefined,
                        background: growthConstrained ? 'rgba(237, 137, 54, 0.22)' : undefined,
                        borderLeft: m.month === forecastMonth ? `2px solid ${BRAND}` : undefined,
                        cursor: breakdownTip ? 'help' : undefined,
                        textDecoration: breakdownTip ? 'underline dotted' : undefined,
                        textUnderlineOffset: breakdownTip ? '3px' : undefined,
                      }}
                    >
                      {breakdownTip ? (
                        <Tooltip
                          label={
                            <Box whiteSpace="pre-line" fontSize="xs" textAlign="left">
                              {breakdownTip}
                            </Box>
                          }
                          hasArrow
                          placement="top"
                          openDelay={200}
                        >
                          <Box as="span">{cell}</Box>
                        </Tooltip>
                      ) : (
                        cell
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

MonthMatrix.propTypes = {
  months: PropTypes.array,
  showCustomerNames: PropTypes.bool,
};

const COMPANY_CASH_STORAGE_PREFIX = 'sourcesUsesCompanyCash:';
const FROM_MONTH_STORAGE_PREFIX = 'sourcesUsesFromMonth:';

// The From month a viewer picked, so Save & Run and page reloads keep it.
function readStoredFromMonth(tenant) {
  try {
    return window.localStorage.getItem(`${FROM_MONTH_STORAGE_PREFIX}${tenant}`) || '';
  } catch {
    return '';
  }
}

function storeFromMonth(tenant, month) {
  try {
    const key = `${FROM_MONTH_STORAGE_PREFIX}${tenant}`;
    if (month) window.localStorage.setItem(key, month);
    else window.localStorage.removeItem(key);
  } catch {
    // Storage can be blocked; From still works for this visit.
  }
}

// Company cash inputs used to live in the browser. They are read once, when Main has
// none on the server yet, so they can be saved there.
function readLegacyCashInputs(tenant) {
  try {
    const raw = window.localStorage.getItem(`${COMPANY_CASH_STORAGE_PREFIX}${tenant}`);
    return raw ? withLenders(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function forgetLegacyCashInputs(tenant) {
  try {
    window.localStorage.removeItem(`${COMPANY_CASH_STORAGE_PREFIX}${tenant}`);
  } catch {
    // Storage can be blocked; nothing to forget then.
  }
}

const EMPTY_SETTINGS = {
  spendPerMonth: 0,
  startMonth: '',
  genericVector: [],
  defaultMarketingSpend: 0,
  defaultMarketingGrowth: 0,
  lastFundingMonth: '',
};

// CashFlowFeeExposurePanel shows Main, a scenario being made, or a finished scenario.
//   mode 'main': Main's data, editable; Save & Run updates Main only.
//   mode 'edit': a new or unfinished scenario; Save & Run updates Main and the scenario.
//   mode 'view': a finished scenario, read-only, from the saved document only.
const CashFlowFeeExposurePanel = forwardRef(function CashFlowFeeExposurePanel({
  tenantFilter,
  screen = 'cashflow',
  mode = 'main',
  scenario = null,
  scenarioId = '',
  scenarioName = '',
  onScenarioSaved,
  onDirtyChange,
}, ref) {
  const readOnly = mode === 'view';
  const userName = useUserName();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedStages, setSelectedStages] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [feeMethod, setFeeMethod] = useState('earned');
  const [dealTerms, setDealTerms] = useState({});
  const [dealsLoading, setDealsLoading] = useState(false);
  const [pipelineClients, setPipelineClients] = useState([]);
  const [liveExposureCustomers, setLiveExposureCustomers] = useState([]);
  const [pipelineSettings, setPipelineSettings] = useState({
    spendPerMonth: 0,
    startMonth: '',
    genericVector: [],
    defaultMarketingSpend: 0,
    defaultMarketingGrowth: 0,
    lastFundingMonth: '',
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [recalculating, setRecalculating] = useState(false);
  const forceRecalcRef = useRef(false);
  const [companyCashInputs, setCompanyCashInputs] = useState(EMPTY_COMPANY_CASH_INPUTS);
  const [companyCashSaved, setCompanyCashSaved] = useState(EMPTY_COMPANY_CASH_INPUTS);
  const [savingCompanyCash, setSavingCompanyCash] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const runRef = useRef(null);
  const runFinishedRef = useRef(null);
  const pendingScenarioSaveRef = useRef(false);
  const companyCashRef = useRef(companyCashInputs);
  companyCashRef.current = companyCashInputs;
  const cashDirty = companyCashInputs !== companyCashSaved;
  // A new scenario is not saved until its first Save & Run.
  const unsavedScenario = mode === 'edit' && !scenarioId;
  const dirty = !readOnly && (configDirty || cashDirty || unsavedScenario);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Main's company cash comes from the server; a finished scenario uses its own copy.
  useEffect(() => {
    if (mode === 'view') {
      const inputs = withLenders(scenario?.companyCash);
      setCompanyCashInputs(inputs);
      setCompanyCashSaved(inputs);
      return undefined;
    }
    let cancelled = false;
    getSourcesUsesCompanyCashApi({ tenant: tenantFilter })
      .then((doc) => {
        if (cancelled) return;
        const stored = doc?.inputs && Object.keys(doc.inputs).length ? withLenders(doc.inputs) : null;
        const saved = stored || EMPTY_COMPANY_CASH_INPUTS;
        setCompanyCashSaved(saved);
        // Inputs typed before they were stored on the server show as unsaved changes.
        setCompanyCashInputs(stored || readLegacyCashInputs(tenantFilter) || saved);
      })
      .catch(() => {
        if (!cancelled) toast({ title: 'Error', description: 'Failed to load the company cash inputs', status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [mode, scenario, tenantFilter, toast]);

  const changeStartMonth = (month) => {
    setStartMonth(month);
    storeFromMonth(tenantFilter, month);
  };

  const saveCompanyCash = async () => {
    const inputs = companyCashRef.current;
    setSavingCompanyCash(true);
    try {
      await saveSourcesUsesCompanyCashApi({
        tenant: tenantFilter,
        scenarioId: mode === 'edit' ? scenarioId || undefined : undefined,
        inputs,
      });
      setCompanyCashSaved(inputs);
      forgetLegacyCashInputs(tenantFilter);
      toast({ title: 'Company cash saved', status: 'success', duration: 3000 });
      return true;
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to save the company cash inputs', status: 'error' });
      return false;
    } finally {
      setSavingCompanyCash(false);
    }
  };

  const finishRun = (ok) => {
    const resolve = runFinishedRef.current;
    runFinishedRef.current = null;
    resolve?.(ok);
  };

  // After Save & Run in a scenario: store the results in the scenario (created on
  // the first run, overwritten after that). The server also saves the company cash to Main.
  const saveScenarioAfterRun = async (shownWindow) => {
    const inputs = companyCashRef.current;
    const body = {
      tenant: tenantFilter,
      name: scenarioName,
      startMonth: shownWindow.startMonth,
      endMonth: shownWindow.endMonth,
      companyCash: inputs,
    };
    try {
      const saved = scenarioId
        ? await overwriteSourcesUsesScenarioApi({ scenarioId, ...body })
        : await createSourcesUsesScenarioApi(body);
      setCompanyCashSaved(inputs);
      forgetLegacyCashInputs(tenantFilter);
      onScenarioSaved?.(saved);
      toast({ title: `Saved scenario ${saved?.name || scenarioName}`, status: 'success', duration: 3000 });
      return true;
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to save the scenario', status: 'error' });
      return false;
    }
  };

  // Save and leave: run Save & Run when Configuration changed, otherwise save the
  // company cash. Resolves false when nothing could be saved.
  const saveAll = useCallback(async () => {
    if (readOnly) return true;
    if (configDirty || unsavedScenario) {
      const finished = new Promise((resolve) => {
        runFinishedRef.current = resolve;
      });
      const started = runRef.current ? runRef.current() : false;
      if (!started) {
        runFinishedRef.current = null;
        return false;
      }
      return finished;
    }
    if (cashDirty) return saveCompanyCash();
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, configDirty, cashDirty, unsavedScenario, mode, scenarioId, scenarioName, tenantFilter]);

  useImperativeHandle(ref, () => ({ saveAll }), [saveAll]);

  // A finished scenario shows only what it saved.
  useEffect(() => {
    if (mode !== 'view' || !scenario) return;
    const exposure = scenario.exposure || {};
    const list = exposure.customers || [];
    setCustomers(list);
    setSelectedIds(list.map((c) => c.customerId));
    setSelectedStages(null);
    setPipelineClients(exposure.pipelineClients || []);
    setLiveExposureCustomers(exposure.liveCustomers || []);
    setPipelineSettings(exposure.pipelineSettings || EMPTY_SETTINGS);
    setActiveTab(0);
    const horizon = resolveHorizon(aggregateSeries(list));
    setStartMonth(scenario.startMonth || horizon.startMonth);
    setEndMonth(scenario.endMonth || horizon.endMonth);
    setLoading(false);
  }, [mode, scenario]);

  useEffect(() => {
    if (mode === 'view') return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const recalc = forceRecalcRef.current;
      forceRecalcRef.current = false;
      const saveScenario = pendingScenarioSaveRef.current;
      pendingScenarioSaveRef.current = false;
      let ok = false;
      try {
        const data = await getCashFlowFeeExposureApi({ tenant: tenantFilter, recalc });
        if (cancelled) return;
        const list = data?.customers || [];
        setCustomers(list);
        setSelectedIds(list.map((c) => c.customerId));
        setSelectedStages(null);
        setPipelineClients(data?.pipelineClients || []);
        setLiveExposureCustomers(data?.liveCustomers || []);
        setPipelineSettings(data?.pipelineSettings || EMPTY_SETTINGS);
        setActiveTab(0);
        const allMonths = aggregateSeries(list);
        const horizon = resolveHorizon(allMonths);
        const savedFundingEnd = data?.pipelineSettings?.lastFundingMonth || '';
        const shownWindow = {
          startMonth: readStoredFromMonth(tenantFilter) || horizon.startMonth,
          endMonth: savedFundingEnd || horizon.endMonth,
        };
        setStartMonth(shownWindow.startMonth);
        setEndMonth(shownWindow.endMonth);
        ok = true;
        if (saveScenario) ok = await saveScenarioAfterRun(shownWindow);
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to get cash flow fee exposure data',
            status: 'error',
          });
          setCustomers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
        if (recalc) finishRun(ok);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantFilter, toast, reloadKey, mode]);

  const chartCustomers = useMemo(
    () => customers.filter((customer) => shownChartCustomer(customer, pipelineClients, tenantFilter)),
    [customers, pipelineClients, tenantFilter]
  );

  const stageOptions = useMemo(() => {
    const fromChart = chartCustomers.map((customer) => customerStage(customer, pipelineClients));
    const fromPipeline = pipelineClients
      .filter((client) => shownPipelineClient(client, tenantFilter))
      .map((client) => (client.future ? 'Future' : String(client.stage || '').trim() || 'Unspecified'));
    return orderedStages([...fromChart, ...fromPipeline]);
  }, [chartCustomers, pipelineClients, tenantFilter]);

  const activeStages = useMemo(
    () => (selectedStages == null
      ? stageOptions
      : selectedStages.filter((stage) => stageOptions.includes(stage))),
    [selectedStages, stageOptions]
  );

  const stageCustomers = useMemo(
    () => chartCustomers.filter((customer) => activeStages.includes(customerStage(customer, pipelineClients))),
    [chartCustomers, pipelineClients, activeStages]
  );

  const selectedCustomers = useMemo(
    () => stageCustomers.filter((customer) => selectedIds.includes(customer.customerId)),
    [stageCustomers, selectedIds]
  );

  const tabCustomers = selectedCustomers;

  const visibleMonthsFor = (sourceCustomers) => {
    const months = aggregateSeries(sourceCustomers);
    return {
      months: filterMonthsByDateRange(months, startMonth, endMonth),
    };
  };

  // Facility fee needs each live customer's latest deal. A finished scenario has them
  // saved; otherwise use the ones the Facility fee tab loaded and fetch the rest.
  const dealTermsForExport = async (liveForExport) => {
    if (readOnly) return scenario?.facilityDeals || {};
    const missing = liveForExport.filter((customer) => !(customer.customerId in dealTerms));
    const fetched = await Promise.all(missing.map(async (customer) => {
      try {
        const deals = await getDealsApi({ customerId: customer.customerId });
        return [customer.customerId, pickLatestDeal(Array.isArray(deals) ? deals : [])];
      } catch {
        return [customer.customerId, null];
      }
    }));
    return { ...dealTerms, ...Object.fromEntries(fetched) };
  };

  const exportViewName = mode === 'main'
    ? 'Main (current data, not a scenario)'
    : `Scenario: ${scenarioName || scenario?.name || ''}${readOnly ? ' (finished)' : ''}`;

  const downloadExcel = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const usedNames = new Set();
    const exportCustomers = selectedCustomers.filter((customer) => (
      shownChartCustomer(customer, pipelineClients, tenantFilter)
    ));
    const combinedMonths = visibleMonthsFor(exportCustomers).months;
    const exportInput = {
      customers: exportCustomers,
      pipelineClients,
      settings: pipelineSettings,
      tenantFilter,
      months: combinedMonths.map((month) => month.month),
    };
    const monthKeys = exportInput.months;

    // 1. Company cash, as a live model with formulas (same customers as the tab).
    writeCompanyCashSheet(workbook.addWorksheet(excelSheetName('Company cash', usedNames)), {
      series: customerCashSeries(chartCustomers, monthRange(startMonth, endMonth)),
      inputs: companyCashInputs,
    });
    // 2. S&U
    writeSourcesAndUsesSheet(workbook.addWorksheet(excelSheetName('S&U', usedNames)), exportInput);
    // 3. Cashflow
    writeCashFlowExportSheet(workbook.addWorksheet(excelSheetName('Cashflow', usedNames)), combinedMonths);
    // 4. Fee, with the calculation chosen on the Fee tab
    writeFeeSheet(workbook.addWorksheet(excelSheetName('Fee', usedNames)), {
      customers: exportCustomers,
      months: monthKeys,
      method: feeMethod,
    });
    // 5. Facility fee
    const exportLive = exportCustomers.filter((customer) => customerStage(customer, pipelineClients) === 'Live');
    const exportDealTerms = await dealTermsForExport(exportLive);
    writeFacilityFeeSheet(workbook.addWorksheet(excelSheetName('Facility fee', usedNames)), {
      customers: exportLive,
      dealTerms: exportDealTerms,
      months: facilityFeeMonthKeys(exportLive, exportDealTerms, startMonth, endMonth),
    });
    // 6. Marketing & Funding
    writeMarketingSheet(workbook.addWorksheet(excelSheetName('Marketing', usedNames)), {
      customers: exportCustomers,
      pipelineClients,
    });
    writeFundingSheet(workbook.addWorksheet(excelSheetName('Funding', usedNames)), exportInput);
    writeRepaymentVectorsSheet(workbook.addWorksheet(excelSheetName('Repayment vectors', usedNames)), {
      customers: exportCustomers,
      pipelineClients,
      settings: pipelineSettings,
    });
    // 7. The detail sheets
    writeCohortRepaymentExportSheet(
      workbook.addWorksheet(excelSheetName('Cashflow cohort repayment', usedNames)),
      combinedMonths
    );
    writePipelineRepaymentSheet(workbook.addWorksheet(excelSheetName('Pipeline repayment', usedNames)), exportInput);
    writeLiveRepaymentSheet(workbook.addWorksheet(excelSheetName('Live repayment', usedNames)), exportInput);
    exportCustomers.forEach((customer) => {
      const { months } = visibleMonthsFor([customer]);
      const sheetName = excelSheetName(customer.companyName || customer.customerId || 'Customer', usedNames);
      writeCashFlowExportSheet(workbook.addWorksheet(sheetName), months, {
        includeGrowthConstraintNotes: true,
      });
    });
    // 8. Configuration
    writeConfigurationSheet(workbook.addWorksheet(excelSheetName('Configuration', usedNames)), {
      viewName: exportViewName,
      tenant: tenantFilter,
      startMonth,
      endMonth,
      settings: pipelineSettings,
      liveCustomers: liveExposureCustomers,
      pipelineClients,
      companyCash: companyCashInputs,
      userName,
    });
    workbook.calcProperties.fullCalcOnLoad = true;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = mode === 'main' ? 'sources_and_uses_main.xlsx' : `sources_and_uses_${(scenarioName || scenario?.name || 'scenario').replace(/[^\w-]+/g, '_')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onSelectedChange = (ids) => {
    const visibleIds = new Set(stageCustomers.map((customer) => customer.customerId));
    const kept = selectedIds.filter((id) => !visibleIds.has(id));
    const next = [...kept, ...ids];
    setSelectedIds(next);
    if (activeTab > 0) {
      const activeCustomer = tabCustomers[activeTab - 1];
      if (!activeCustomer || !next.includes(activeCustomer.customerId)) {
        setActiveTab(0);
      }
    }
  };

  // Save & Run saves Configuration to Main: the customers, then the company cash
  // (in a scenario the scenario save does that), then reloads with a recalculation.
  const recalculatePipeline = async (payload) => {
    setRecalculating(true);
    try {
      await savePipelineExposureBatchApi(payload);
      if (mode === 'main' && companyCashRef.current !== companyCashSaved) {
        await saveSourcesUsesCompanyCashApi({ tenant: tenantFilter, inputs: companyCashRef.current });
        setCompanyCashSaved(companyCashRef.current);
        forgetLegacyCashInputs(tenantFilter);
      }
      pendingScenarioSaveRef.current = mode === 'edit';
      forceRecalcRef.current = true;
      setReloadKey((key) => key + 1);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to recalculate',
        status: 'error',
      });
      finishRun(false);
    } finally {
      setRecalculating(false);
    }
  };

  const liveCustomers = useMemo(
    () => chartCustomers.filter((customer) => customerStage(customer, pipelineClients) === 'Live'),
    [chartCustomers, pipelineClients]
  );
  const liveCustomerKey = liveCustomers.map((customer) => customer.customerId).sort().join(',');

  // A finished scenario uses the deal terms saved with it.
  useEffect(() => {
    if (mode !== 'view') return;
    setDealTerms(scenario?.facilityDeals || {});
    setDealsLoading(false);
  }, [mode, scenario]);

  useEffect(() => {
    if (mode === 'view' || screen !== 'facility' || !liveCustomerKey) return undefined;
    let cancelled = false;
    setDealsLoading(true);
    Promise.all(liveCustomerKey.split(',').map(async (customerId) => {
      try {
        const deals = await getDealsApi({ customerId });
        return [customerId, pickLatestDeal(Array.isArray(deals) ? deals : [])];
      } catch {
        return [customerId, null];
      }
    })).then((entries) => {
      if (cancelled) return;
      setDealTerms(Object.fromEntries(entries));
      setDealsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, screen, liveCustomerKey]);

  const showAnalysis = ['cashflow', 'su', 'fee', 'facility', 'marketing'].includes(screen);
  const screenTitle = {
    configuration: 'Configuration',
    marketing: 'Marketing & Funding',
    cashflow: 'Cashflow',
    su: 'S&U',
    fee: 'Fee',
    facility: 'Facility fee',
    'company-cash': 'Company cash',
  }[screen] || 'Cashflow';
  const feeMethodHelp = {
    earned: 'Fee is earned in the month it accrues, the same amount as on Cashflow.',
    end: 'Each cohort’s fee is added up and shown only in the month of that cohort’s last repayment.',
    prorata: 'Each cohort’s fee is its share of principal plus fee. That percent of every repayment is the fee for that month.',
  }[feeMethod];

  return (
    <Card mt={8} p={5} borderRadius="20px">
      <Box position="sticky" top="100px" zIndex={3} bg="white" mb={4}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
        <Flex align="center" gap={2} wrap="wrap">
          <Text fontSize="xl" fontWeight={700}>
            {screenTitle}
          </Text>
          <Tooltip
            hasArrow
            placement="top"
            openDelay={200}
            label="Projects funding, fee, returns, and exposure. Live customers use their deals. Non-live customers in the table are added to the total and their own tab when Include is checked."
          >
            <InfoOutlineIcon color="gray.500" boxSize={4} cursor="help" aria-label="Cash flow overview" />
          </Tooltip>
          <HelpLink
            sectionId="sources-uses-cash-flow"
            variant="subtle"
            fontSize="sm"
          >
            How it works
          </HelpLink>
          <HelpLink sectionId="sources-uses-fee" variant="subtle" fontSize="sm">
            Fee
          </HelpLink>
          <HelpLink sectionId="sources-uses-repayment-vector" variant="subtle" fontSize="sm">
            Repayment vector
          </HelpLink>
        </Flex>
        {!loading && showAnalysis && chartCustomers.length > 0 && (
          <Flex gap={3} align="center" wrap="wrap">
            <CustomerMultiSelect
              customers={stageCustomers}
              selectedIds={selectedCustomers.map((customer) => customer.customerId)}
              onChange={onSelectedChange}
            />
            <StageMultiSelect
              stages={stageOptions}
              selectedStages={selectedStages}
              onChange={(next) => {
                setSelectedStages(next);
                setActiveTab(0);
              }}
            />
            <Button size="sm" colorScheme="brand" onClick={() => downloadExcel()}>
              Export
            </Button>
          </Flex>
        )}
        {!loading && !showAnalysis && (
          <Button size="sm" colorScheme="brand" onClick={() => downloadExcel()}>
            Export
          </Button>
        )}
      </Flex>
      {!loading && screen === 'fee' && chartCustomers.length > 0 && (
        <Flex align="center" gap={3} mt={3} wrap="wrap">
          <Text fontSize="sm" fontWeight={600}>Fee calculation</Text>
          <Select
            size="sm"
            w="220px"
            aria-label="Fee calculation"
            value={feeMethod}
            onChange={(event) => setFeeMethod(event.target.value)}
          >
            {FEE_METHODS.map((method) => (
              <option key={method.id} value={method.id}>{method.label}</option>
            ))}
          </Select>
        </Flex>
      )}
      {!loading && screen === 'fee' && (
        <Text fontSize="sm" color="gray.600" mt={3}>{feeMethodHelp}</Text>
      )}
      {!loading && screen === 'facility' && (
        <Text fontSize="sm" color="gray.600" mt={3}>
          Live customers only. Each customer’s facility fees are added up and split equally across the commitment months on their deal. A deal without a commitment shows the fees in the months they are charged.
        </Text>
      )}
      </Box>

      {loading && (
        <Flex justify="center" py={16}>
          <Spinner />
        </Flex>
      )}

      {!loading && showAnalysis && chartCustomers.length === 0 && (
        <Text color="gray.500">No clients are included in the chart yet.</Text>
      )}

      {!loading && showAnalysis && chartCustomers.length > 0 && selectedCustomers.length === 0 && (
        <Text color="gray.500">No clients match the selected stages.</Text>
      )}

      {!loading && screen === 'cashflow' && selectedCustomers.length > 0 && (
        <Tabs index={activeTab} onChange={setActiveTab} variant="soft-rounded" colorScheme="brand">
          <TabList flexWrap="wrap" gap={1}>
            <Tab>Combined</Tab>
            {tabCustomers.map((c) => (
              <Tab key={c.customerId}>{c.companyName}</Tab>
            ))}
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              {(() => {
                const { months } = visibleMonthsFor(selectedCustomers);
                return (
                  <>
                    <TrendChart months={months} />
                    <MonthMatrix months={months} showCustomerNames />
                  </>
                );
              })()}
            </TabPanel>
            {tabCustomers.map((c) => {
              const { months } = visibleMonthsFor([c]);
              return (
                <TabPanel key={c.customerId} px={0}>
                  <TrendChart months={months} />
                  <MonthMatrix months={months} />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      )}

      {!loading && screen === 'cashflow' && tabCustomers.length > 0 && (() => {
        const activeCustomer = activeTab > 0 ? tabCustomers[activeTab - 1] : null;
        const chartCustomers = activeCustomer ? [activeCustomer] : selectedCustomers;
        return (
          <CohortRepaymentSection
            customer={activeCustomer || { months: aggregateSeries(selectedCustomers) }}
            customers={chartCustomers}
            months={visibleMonthsFor(chartCustomers).months}
            showCohortRepayments={!!activeCustomer}
          />
        );
      })()}

      {!loading && screen === 'su' && selectedCustomers.length > 0 && (
        <SourcesAndUsesTable
          customers={selectedCustomers}
          pipelineClients={pipelineClients}
          months={visibleMonthsFor(selectedCustomers).months}
          tenantFilter={tenantFilter}
        />
      )}

      {!loading && screen === 'marketing' && selectedCustomers.length > 0 && (
        <MarketingUsedTable
          customers={selectedCustomers}
          pipelineClients={pipelineClients}
          settings={pipelineSettings}
          startMonth={startMonth}
          endMonth={endMonth}
        />
      )}

      {!loading && screen === 'fee' && selectedCustomers.length > 0 && (
        <FeeCalculationTable
          customers={selectedCustomers}
          months={visibleMonthsFor(selectedCustomers).months}
          method={feeMethod}
        />
      )}

      {!loading && screen === 'facility' && selectedCustomers.length > 0 && (() => {
        const shownLiveCustomers = selectedCustomers.filter(
          (customer) => customerStage(customer, pipelineClients) === 'Live'
        );
        if (!shownLiveCustomers.length) {
          return <Text color="gray.500">No live customers match the selected filters.</Text>;
        }
        if (readOnly && !scenario?.facilityDeals) {
          return <Text color="gray.500">This scenario was saved without facility data.</Text>;
        }
        if (!readOnly && (dealsLoading || shownLiveCustomers.some((customer) => !(customer.customerId in dealTerms)))) {
          return (
            <Flex justify="center" py={16}>
              <Spinner />
            </Flex>
          );
        }
        const months = facilityFeeMonthKeys(shownLiveCustomers, dealTerms, startMonth, endMonth);
        if (!months.length) {
          return (
            <Text color="gray.500">
              These live customers have no commitment length on their deal.
            </Text>
          );
        }
        return (
          <FacilityFeeTable
            customers={shownLiveCustomers}
            dealTerms={dealTerms}
            months={months}
          />
        );
      })()}

      {!loading && screen === 'company-cash' && (
        <Box>
          {!readOnly && (
            <Flex align="center" gap={3} wrap="wrap" mb={4}>
              <Button
                size="sm"
                colorScheme="brand"
                isDisabled={!cashDirty}
                isLoading={savingCompanyCash}
                onClick={saveCompanyCash}
              >
                Save
              </Button>
              <Text fontSize="sm" color={cashDirty ? 'orange.600' : 'gray.500'}>
                {cashDirty
                  ? 'Unsaved changes.'
                  : 'Saved.'}
                {mode === 'edit' && scenarioId
                  ? ' Save writes these inputs to this scenario and to Main.'
                  : ' Save writes these inputs to Main.'}
              </Text>
            </Flex>
          )}
          <CompanyCashStatement
            customers={chartCustomers}
            startMonth={startMonth}
            endMonth={endMonth}
            inputs={companyCashInputs}
            onInputsChange={readOnly ? undefined : setCompanyCashInputs}
            readOnly={readOnly}
          />
        </Box>
      )}

      {!loading && (
        <Box mt={screen === 'configuration' ? 0 : 8} display={screen === 'configuration' ? 'block' : 'none'}>
          <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em" mb={3}>1. CUSTOMERS</Text>
          <PipelineClientsTable
            clients={pipelineClients}
            liveCustomers={liveExposureCustomers}
            settings={pipelineSettings}
            tenant={tenantFilter}
            fundingEndMonth={endMonth}
            startMonth={startMonth}
            endMonth={endMonth}
            onStartMonthChange={changeStartMonth}
            onEndMonthChange={setEndMonth}
            showToolbar={screen === 'configuration' && !readOnly}
            onRecalculate={recalculatePipeline}
            recalculating={recalculating}
            readOnly={readOnly}
            onDirtyChange={setConfigDirty}
            runRef={runRef}
          />
          {screen === 'configuration' && (
            <>
              <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em" mb={1}>2. COMPANY CASH</Text>
              <Text fontSize="sm" color="gray.600" mb={3}>
                {readOnly
                  ? 'The company cash inputs saved with this scenario.'
                  : 'Saved with Save & Run, or with Save on Company cash. The statement is on Company cash.'}
              </Text>
              <CompanyCashStatement
                customers={chartCustomers}
                startMonth={startMonth}
                endMonth={endMonth}
                inputs={companyCashInputs}
                onInputsChange={readOnly ? undefined : setCompanyCashInputs}
                readOnly={readOnly}
                inputsOnly
              />
              {!readOnly && (
                // The same Save & Run as the button at the top, so there is no need to scroll back up.
                <Flex justify="flex-end" mt={4}>
                  <Button
                    size="sm"
                    colorScheme="brand"
                    isLoading={recalculating}
                    onClick={() => runRef.current?.()}
                  >
                    Save & Run
                  </Button>
                </Flex>
              )}
            </>
          )}
        </Box>
      )}
    </Card>
  );
});

CashFlowFeeExposurePanel.propTypes = {
  tenantFilter: PropTypes.string.isRequired,
  screen: PropTypes.oneOf(['configuration', 'marketing', 'cashflow', 'su', 'fee', 'facility', 'company-cash']),
  mode: PropTypes.oneOf(['main', 'edit', 'view']),
  scenario: PropTypes.object,
  scenarioId: PropTypes.string,
  scenarioName: PropTypes.string,
  onScenarioSaved: PropTypes.func,
  onDirtyChange: PropTypes.func,
};

export default CashFlowFeeExposurePanel;
